import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus, Search, Trash2, ExternalLink, Filter, X,
  ArrowUpDown, ArrowUp, ArrowDown, Pencil, ClipboardList,
} from "lucide-react";
import type { LateralCandidate, LateralJobPosition } from "@/types/database";
import { LATERAL_STATUS_OPTIONS, LATERAL_STATUS_COLORS, getLateralStatusLabel } from "@/lib/lateral-statuses";
import { LateralFilterBar } from "@/components/lateral/LateralFilterBar";
import { LateralStatusBadge } from "@/components/lateral/LateralStatusBadge";
import { LateralSavedFiltersBar } from "@/components/lateral/LateralSavedFiltersBar";
import { useLateralListFilters, SortField } from "@/hooks/useLateralListFilters";
import { emptyLateralFilters } from "@/components/lateral/LateralFilterBar";

function SortButton({
  field, label, sortBy, sortOrder, onSort,
}: {
  field: SortField; label: string; sortBy: SortField;
  sortOrder: "asc" | "desc"; onSort: (f: SortField) => void;
}) {
  const active = sortBy === field;
  return (
    <button
      className="flex items-center gap-1 hover:text-gray-900 transition-colors"
      onClick={() => onSort(field)}
    >
      {label}
      {active
        ? sortOrder === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
        : <ArrowUpDown className="h-3 w-3 opacity-40" />}
    </button>
  );
}

export default function LateralCandidatesPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { profile } = useAuth();
  const isAdmin = profile?.role === "admin";
  const queryClient = useQueryClient();
  const [showFilters, setShowFilters] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [pendingDeleteName, setPendingDeleteName] = useState<string>("");

  const { data: positions } = useQuery({
    queryKey: ["lateral-job-positions-active"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("lateral_job_positions")
        .select("id, job_title")
        .eq("is_active", true)
        .order("job_title");
      if (error) throw error;
      return data as Pick<LateralJobPosition, "id" | "job_title">[];
    },
  });

  const { data: candidates, isLoading } = useQuery({
    queryKey: ["lateral-candidates"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("lateral_candidates")
        .select("*, lateral_job_positions(job_title)")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as (LateralCandidate & { lateral_job_positions?: { job_title: string } | null })[];
    },
  });

  const {
    search, setSearch,
    filters, setFilter,
    clearFilters,
    filtered,
    hasActiveFilters,
    activeFilterCount,
    sortBy, sortOrder, handleSort,
  } = useLateralListFilters(candidates);

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("lateral_candidates").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lateral-candidates"] });
      toast({ title: "Candidate deleted" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const updateStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase
        .from("lateral_candidates")
        .update({ status, updated_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["lateral-candidates"] }),
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Lateral Candidates</h1>
          <p className="text-sm text-gray-500 mt-1">
            {filtered.length} of {candidates?.length ?? 0} candidates
          </p>
        </div>
        <Button onClick={() => navigate("/admin/lateral/candidates/create")}>
          <Plus className="h-4 w-4 mr-2" />
          Add Candidate
        </Button>
      </div>

      {/* Search + filter toggle */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search name, email, company, location..."
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Button
          variant="outline"
          onClick={() => setShowFilters((v) => !v)}
          className="gap-2"
        >
          <Filter className="h-4 w-4" />
          Filters
          {activeFilterCount > 0 && (
            <span className="bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {activeFilterCount}
            </span>
          )}
        </Button>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1 text-gray-500">
            <X className="h-4 w-4" />
            Clear
          </Button>
        )}
      </div>

      {/* Saved filters row — always visible */}
      <LateralSavedFiltersBar
        context="list"
        currentFilters={{ search, ...filters }}
        onApply={(saved) => {
          if (saved.search !== undefined) setSearch(saved.search);
          const { search: _s, ...rest } = saved;
          const base = emptyLateralFilters();
          Object.keys(base).forEach((k) => {
            const key = k as keyof typeof base;
            if (rest[key] !== undefined) setFilter(key, rest[key]);
          });
        }}
        onClear={clearFilters}
      />

      {/* Reactive filter panel */}
      {showFilters && (
        <LateralFilterBar
          values={filters}
          onChange={setFilter}
          positions={positions ?? []}
          showStatus
          onClear={clearFilters}
        />
      )}

      {/* Table */}
      <div className="bg-white rounded-lg border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="text-xs text-gray-500">
              <TableHead>
                <SortButton field="full_name" label="Name" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
              </TableHead>
              <TableHead>Company / Role</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>
                <SortButton field="total_experience_years" label="Exp" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
              </TableHead>
              <TableHead>Position Applied</TableHead>
              <TableHead>
                <SortButton field="expected_ctc" label="Exp CTC" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
              </TableHead>
              <TableHead>Notice</TableHead>
              <TableHead>
                <SortButton field="cv_score" label="Score" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
              </TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-20">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={10} className="text-center py-8 text-gray-500">Loading...</TableCell>
              </TableRow>
            )}
            {!isLoading && !filtered.length && (
              <TableRow>
                <TableCell colSpan={10} className="text-center py-8 text-gray-500">
                  {hasActiveFilters ? "No candidates match the current filters." : "No candidates yet."}
                </TableCell>
              </TableRow>
            )}
            {filtered.map((c) => (
              <TableRow key={c.id}>
                <TableCell>
                  <div>
                    <p className="font-medium text-sm">{c.full_name}</p>
                    <p className="text-xs text-gray-400">{c.email}</p>
                  </div>
                </TableCell>
                <TableCell>
                  <div>
                    <p className="text-sm">{c.current_company || "—"}</p>
                    <p className="text-xs text-gray-400">{c.current_designation || ""}</p>
                  </div>
                </TableCell>
                <TableCell className="text-xs text-gray-600">
                  {c.current_location || "—"}
                </TableCell>
                <TableCell className="text-sm">
                  {c.total_experience_years != null ? `${c.total_experience_years} yrs` : "—"}
                </TableCell>
                <TableCell className="text-sm">
                  {(c as any).lateral_job_positions?.job_title || "—"}
                </TableCell>
                <TableCell className="text-sm text-blue-700">
                  {c.expected_ctc || "—"}
                </TableCell>
                <TableCell className="text-xs text-gray-600">
                  {c.notice_period || "—"}
                </TableCell>
                <TableCell>
                  {c.cv_score != null ? (
                    <span className={`font-semibold text-sm ${
                      c.cv_score >= 70 ? "text-green-600" : c.cv_score >= 50 ? "text-yellow-600" : "text-red-600"
                    }`}>
                      {c.cv_score}/100
                    </span>
                  ) : (
                    <span className="text-gray-400 text-sm">—</span>
                  )}
                </TableCell>
                <TableCell>
                  <Select
                    value={c.status}
                    onValueChange={(v) => updateStatus.mutate({ id: c.id, status: v })}
                  >
                    <SelectTrigger className="h-7 w-40 text-xs border-0 shadow-none p-0 focus:ring-0">
                      <LateralStatusBadge status={c.status} />
                    </SelectTrigger>
                    <SelectContent>
                      {LATERAL_STATUS_OPTIONS.map((s) => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost" size="icon" className="h-7 w-7" title="Evaluate"
                      onClick={() => navigate(`/admin/lateral/candidates/${c.id}/evaluate`)}
                    >
                      <ClipboardList className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost" size="icon" className="h-7 w-7" title="Edit"
                      onClick={() => navigate(`/admin/lateral/candidates/${c.id}/edit`)}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    {c.resume_url && (
                      <a href={c.resume_url} target="_blank" rel="noopener noreferrer">
                        <Button variant="ghost" size="icon" className="h-7 w-7" title="View Resume">
                          <ExternalLink className="h-3.5 w-3.5" />
                        </Button>
                      </a>
                    )}
                    {isAdmin && (
                      <Button
                        variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600"
                        title="Delete candidate"
                        onClick={() => {
                          setPendingDeleteId(c.id);
                          setPendingDeleteName(c.full_name);
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <AlertDialog
        open={!!pendingDeleteId}
        onOpenChange={(open) => { if (!open) setPendingDeleteId(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete candidate?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>{pendingDeleteName}</strong>. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              disabled={remove.isPending}
              onClick={(e) => {
                e.preventDefault();
                const idToDelete = pendingDeleteId;
                setPendingDeleteId(null);
                if (idToDelete) remove.mutate(idToDelete);
              }}
            >
              {remove.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
